import type { DesktopFrameMessage } from "../../shared/schemas";
import type { ActiveAppFrame } from "../app-frames";
import { activationError, closeError, operationError } from "./broker-core";
import {
  DesktopCapabilityResponseMessageSchema,
  DesktopCloseResponseMessageSchema,
  DesktopOperationResponseMessageSchema,
  DESKTOP_PROTOCOL_VERSION,
} from "../../shared/schemas";

export type AdmissionFailure = {
  admitted: false;
  code: "CLIENT_BUSY" | "REQUEST_RATE_LIMITED" | "REQUEST_REPLAYED";
  message: string;
  retryable: boolean;
};

export type AdmissionDecision = { admitted: true } | AdmissionFailure;

export class DesktopAdmissionController {
  readonly #seenRequestIds = new Map<WindowProxy, string[]>();
  readonly #requestTimes = new Map<WindowProxy, number[]>();
  readonly #inFlight = new Map<WindowProxy, number>();

  admit(source: WindowProxy, requestId: string, ownedCleanup: boolean): AdmissionDecision {
    const replay = this.#recordRequestId(source, requestId);
    if (replay) return replay;
    const rateLimit = ownedCleanup ? null : this.#recordRequestTime(source);
    if (rateLimit) return rateLimit;
    const inFlight = this.#inFlight.get(source) ?? 0;
    if (inFlight >= (ownedCleanup ? 5 : 4)) return busyFailure();
    this.#inFlight.set(source, inFlight + 1);
    return { admitted: true };
  }

  settle(source: WindowProxy): void {
    const current = this.#inFlight.get(source) ?? 1;
    if (current <= 1) this.#inFlight.delete(source);
    else this.#inFlight.set(source, current - 1);
  }

  forget(source: WindowProxy): void {
    this.#seenRequestIds.delete(source);
    this.#requestTimes.delete(source);
    this.#inFlight.delete(source);
  }

  #recordRequestId(source: WindowProxy, requestId: string): AdmissionFailure | null {
    const seen = this.#seenRequestIds.get(source) ?? [];
    if (seen.includes(requestId)) return replayFailure();
    seen.push(requestId);
    if (seen.length > 128) seen.shift();
    this.#seenRequestIds.set(source, seen);
    return null;
  }

  #recordRequestTime(source: WindowProxy): AdmissionFailure | null {
    const cutoff = Date.now() - 60_000;
    const recent = (this.#requestTimes.get(source) ?? []).filter((timestamp) => timestamp > cutoff);
    if (recent.length >= 60) return rateLimitFailure();
    recent.push(Date.now());
    this.#requestTimes.set(source, recent);
    return null;
  }
}

function replayFailure(): AdmissionFailure {
  return {
    admitted: false,
    code: "REQUEST_REPLAYED",
    message: "That desktop request identifier has already been used.",
    retryable: false,
  };
}

function rateLimitFailure(): AdmissionFailure {
  return {
    admitted: false,
    code: "REQUEST_RATE_LIMITED",
    message: "This app is sending desktop requests too quickly.",
    retryable: true,
  };
}

function busyFailure(): AdmissionFailure {
  return {
    admitted: false,
    code: "CLIENT_BUSY",
    message: "Four desktop requests are already in progress.",
    retryable: true,
  };
}

export function postAdmissionFailure(input: {
  source: WindowProxy;
  active: ActiveAppFrame;
  message: DesktopFrameMessage;
  failure: AdmissionFailure;
}): void {
  const { source, active, message, failure } = input;
  const error = [failure.code, failure.message, message.requestId, failure.retryable] as const;
  if (message.type === "platform:desktop-capability-request") {
    source.postMessage(
      DesktopCapabilityResponseMessageSchema.parse({
        type: "platform:desktop-capability-response",
        protocolVersion: DESKTOP_PROTOCOL_VERSION,
        requestId: message.requestId,
        result: activationError(...error),
      }),
      active.origin,
    );
    return;
  }
  if (message.type === "platform:desktop-operation-request") {
    source.postMessage(
      DesktopOperationResponseMessageSchema.parse({
        type: "platform:desktop-operation-response",
        protocolVersion: DESKTOP_PROTOCOL_VERSION,
        requestId: message.requestId,
        result: operationError(...error),
      }),
      active.origin,
    );
    return;
  }
  source.postMessage(
    DesktopCloseResponseMessageSchema.parse({
      type: "platform:desktop-capability-close-response",
      protocolVersion: DESKTOP_PROTOCOL_VERSION,
      requestId: message.requestId,
      result: closeError(...error),
    }),
    active.origin,
  );
}
